import { useState, useEffect } from 'react';
import { useToast } from './use-toast';
import { supabase } from '@/integrations/supabase/client';

// Test run interface
export interface TestRun {
  id: string;
  status: string;
  test_type: string;
  started_at: string;
  completed_at?: string;
  triggered_by?: string;
  summary?: any;
  created_at: string;
  updated_at: string;
  environment?: string;
  test_config?: any;
}

// Test result interface
export interface TestResult {
  id: string;
  test_run_id: string;
  suite: string;
  test_name: string;
  status: string;
  duration: number;
  error?: string;
  stack_trace?: string;
  screenshot_id?: string;
  report_url?: string;
  created_at: string;
  metadata?: any;
}

// Test screenshot interface
export interface TestScreenshot {
  id: string;
  test_result_id?: string;
  test_run_id: string;
  file_path: string;
  file_name: string;
  full_page_url?: string;
  thumbnail_url?: string;
  width?: number;
  height?: number;
  device?: string;
  created_at: string;
}

// Options for running tests
export interface RunTestsOptions {
  testType: 'monitoring' | 'health_check' | 'all';
}

// Hook for using the test runner
export function useTestRunner() {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTestRun, setActiveTestRun] = useState<TestRun | null>(null);
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [testScreenshots, setTestScreenshots] = useState<TestScreenshot[]>([]);
  const { toast } = useToast();

  // Function to run tests
  const runTests = async (options: RunTestsOptions) => {
    try {
      setIsLoading(true);
      console.log(`Attempting to run tests of type: ${options.testType}`);
      
      // Get current user ID
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('Error getting user:', userError);
      }
      const userId = userData?.user?.id;
      console.log(`User ID for test run: ${userId || 'anonymous'}`);
      
      // Call our edge function to initiate a test run
      console.log('Invoking execute-tests edge function...');
      const { data, error } = await supabase.functions.invoke('execute-tests', {
        body: {
          testType: options.testType,
          userId: userId
        }
      });
      
      if (error) {
        console.error('Edge function error:', error);
        toast({
          title: 'Error running tests',
          description: error.message,
          variant: 'destructive',
        });
        return null;
      }
      
      console.log('Edge function response:', data);
      
      // Get the test run ID from the response
      const testRunId = data.testRunId;
      console.log(`Test run created with ID: ${testRunId}`);
      
      toast({
        title: 'Tests started',
        description: `Test run ID: ${testRunId}${data.simulated ? ' (Simulation mode)' : ''}`,
      });
      
      // Load the test run details
      console.log('Loading test run details...');
      await loadTestRun(testRunId);
      
      // Start polling for updates
      console.log('Starting to poll for test updates...');
      startPollingTestRun(testRunId);
      
      return testRunId;
    } catch (error) {
      console.error('Error running tests:', error);
      toast({
        title: 'Error running tests',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to load a specific test run
  const loadTestRun = async (id: string) => {
    try {
      setIsLoading(true);
      
      // Get the test run details
      const { data: testRun, error } = await supabase
        .from('test_runs')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) {
        toast({
          title: 'Error loading test run',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }
      
      setActiveTestRun(testRun);
      
      // Load test results
      await loadTestResults(id);
      
      // Load test screenshots
      await loadTestScreenshots(id);
    } catch (error) {
      console.error('Error loading test run:', error);
      toast({
        title: 'Error loading test run',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to load all test runs
  const loadTestRuns = async (limit = 10, offset = 0) => {
    try {
      setIsLoading(true);
      
      // Get all test runs
      const { data, error } = await supabase
        .from('test_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
        
      if (error) {
        toast({
          title: 'Error loading test runs',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }
      
      setTestRuns(data);
    } catch (error) {
      console.error('Error loading test runs:', error);
      toast({
        title: 'Error loading test runs',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Function to load test results for a test run
  const loadTestResults = async (testRunId: string) => {
    try {
      // Get test results for the test run
      const { data, error } = await supabase
        .from('test_results')
        .select('*')
        .eq('test_run_id', testRunId)
        .order('created_at', { ascending: true });
        
      if (error) {
        toast({
          title: 'Error loading test results',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }
      
      setTestResults(data);
    } catch (error) {
      console.error('Error loading test results:', error);
      toast({
        title: 'Error loading test results',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };
  
  // Function to load test screenshots for a test run
  const loadTestScreenshots = async (testRunId: string) => {
    try {
      // Get test screenshots for the test run
      const { data, error } = await supabase
        .from('test_screenshots')
        .select('*')
        .eq('test_run_id', testRunId)
        .order('created_at', { ascending: true });
        
      if (error) {
        toast({
          title: 'Error loading test screenshots',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }
      
      setTestScreenshots(data);
    } catch (error) {
      console.error('Error loading test screenshots:', error);
      toast({
        title: 'Error loading test screenshots',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };
  
  // Start polling for updates to a test run
  const startPollingTestRun = (testRunId: string) => {
    const interval = setInterval(async () => {
      const { data, error } = await supabase
        .from('test_runs')
        .select('*')
        .eq('id', testRunId)
        .single();
        
      if (error) {
        console.error('Error polling test run:', error);
        clearInterval(interval);
        return;
      }
      
      setActiveTestRun(data);
      
      // If the test run is completed or failed, stop polling
      if (['completed', 'failed'].includes(data.status)) {
        clearInterval(interval);
        
        // Load the final results and screenshots
        await loadTestResults(testRunId);
        await loadTestScreenshots(testRunId);
        
        // Show a toast with the results
        const summaryData = data.summary || {};
        // Safely extract values with type checking
        const passedCount = typeof summaryData === 'object' && summaryData !== null && 'passed' in summaryData 
          ? Number(summaryData.passed) || 0 
          : 0;
        const failedCount = typeof summaryData === 'object' && summaryData !== null && 'failed' in summaryData 
          ? Number(summaryData.failed) || 0 
          : 0;
        const skippedCount = typeof summaryData === 'object' && summaryData !== null && 'skipped' in summaryData 
          ? Number(summaryData.skipped) || 0 
          : 0;
        
        toast({
          title: `Test run ${data.status}`,
          description: `Passed: ${passedCount}, Failed: ${failedCount}, Skipped: ${skippedCount}`,
          variant: data.status === 'completed' ? 'default' : 'destructive',
        });
      }
    }, 3000); // Poll every 3 seconds
    
    // Cleanup function to clear the interval
    return () => clearInterval(interval);
  };

  // Set up real-time subscriptions for active test run
  useEffect(() => {
    if (!activeTestRun) return;

    const testRunSubscription = supabase
      .channel('table-db-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'test_runs',
          filter: `id=eq.${activeTestRun.id}`,
        },
        (payload) => {
          setActiveTestRun(payload.new as TestRun);
        }
      )
      .subscribe();

    const testResultsSubscription = supabase
      .channel('table-db-changes-results')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'test_results',
          filter: `test_run_id=eq.${activeTestRun.id}`,
        },
        (payload) => {
          setTestResults((prev) => [...prev, payload.new as TestResult]);
        }
      )
      .subscribe();

    const testScreenshotsSubscription = supabase
      .channel('table-db-changes-screenshots')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'test_screenshots',
          filter: `test_run_id=eq.${activeTestRun.id}`,
        },
        (payload) => {
          setTestScreenshots((prev) => [...prev, payload.new as TestScreenshot]);
        }
      )
      .subscribe();

    return () => {
      testRunSubscription.unsubscribe();
      testResultsSubscription.unsubscribe();
      testScreenshotsSubscription.unsubscribe();
    };
  }, [activeTestRun?.id]);

  return {
    isLoading,
    activeTestRun,
    testRuns,
    testResults,
    testScreenshots,
    runTests,
    loadTestRun,
    loadTestRuns,
    loadTestResults,
    loadTestScreenshots,
  };
}